import { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import OpenInNewTwoToneIcon from '@mui/icons-material/OpenInNewTwoTone';
import { ManagementServices } from 'src/services/ManagementServices';
import { toast } from 'react-toastify';
import moment from 'moment';
import AppPage from 'src/components/AppPage';
import { brand } from 'src/theme/brand';

type BankSlipUploadRow = {
  _id: string;
  userId?: {
    name?: string;
    email?: string;
    shopId?: { shopName?: string };
  };
  shopName?: string;
  category?: string;
  createdAt?: string;
  url?: string;
};

const ExtendedBankPayments = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [uploads, setUploads] = useState<BankSlipUploadRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const res = await ManagementServices.getBankSlipUploadsAdmin(
        page + 1,
        limit
      );
      if (cancelled) return;
      if (res.success && res.data) {
        setUploads(res.data.uploads || []);
        setTotal(res.data.total ?? 0);
      } else {
        setUploads([]);
        setTotal(0);
        toast.error(res.message || 'Failed to load bank slip uploads');
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, limit]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const shopLabel = (row: BankSlipUploadRow) =>
    row.shopName || row.userId?.shopId?.shopName || '-';

  return (
    <AppPage
      title="Admin — Extended bank payments"
      heading="Extended Bank Payments"
      description="Review bank slip uploads submitted by shops and users."
    >
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Bank slip uploads"
              subheader="Admin list of submitted payment slips."
            />
            <Divider />
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="300px"
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>No</TableCell>
                        <TableCell>Shop</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Slip</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: 'text.secondary',
                                py: 4
                              }}
                            >
                              No data to display
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        uploads.map((row, index) => (
                          <TableRow key={row._id} hover>
                            <TableCell>{page * limit + index + 1}</TableCell>
                            <TableCell>{shopLabel(row)}</TableCell>
                            <TableCell>
                              {row.userId?.name ? row.userId.name : '-'}
                            </TableCell>
                            <TableCell>
                              {row.userId?.email ? row.userId.email : '-'}
                            </TableCell>
                            <TableCell>
                              {row.category
                                ? row.category.replace(/_/g, ' ')
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {row.createdAt
                                ? moment(row.createdAt).format(
                                    'MMM Do YYYY, h:mm A'
                                  )
                                : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {row.url ? (
                                <Tooltip title="Open slip" arrow>
                                  <IconButton
                                    component={Link}
                                    href={row.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    sx={{
                                      borderRadius: '8px',
                                      color: brand.blue,
                                      '&:hover': {
                                        background: theme.colors.primary.lighter
                                      }
                                    }}
                                  >
                                    <OpenInNewTwoToneIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={limit}
                  onRowsPerPageChange={handleLimitChange}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              </>
            )}
          </Card>
        </Grid>
      </Grid>
    </AppPage>
  );
};

export default ExtendedBankPayments;
